/* istanbul ignore file */
// import { getVarValue } from '../../../common/utils/Sparky'
import { getTsgToken, getFrameworkServiceUrl } from '@sparky/framework'
import axios from 'axios'
import {
  ACTIVATE_URL,
  CLAIMS_URL, INSTANCE_URL,
  LICENSE_QUOTA_URL,
  LICENSES_URL,
  PAE_VALIDATION_URL,
  RECLAIM_URL,
  SUPPORT_ACCOUNTS,
  VALIDATE_PANORAMA_URL,
  TENANT_DEVICES,
  SUPPORT_DEVICES, APP_DEVICE_ASSOCIATION, MODIFY_SUBSCRIPTION_URL,
  PANORAMA_OTP,
  NOT_ELIGIBLE_APPS_DEVICE_ASSOCIATION,
} from '../../common/utils/ServiceUrls'
import { getVarValue } from '../../common/utils/Sparky'

import MOCK_LIST_DEVICES from '../../common/mock/ListDevices.json'
import { FF_DAS_XSIG, FF_USE_LOCAL_SUBSCRIPTION_SERVICE } from '../../common/utils/FeatureFlags'
import { TSG_NONE } from '../../activation/constants'
import { DAS_FILTER_DEVICE_TYPES } from '../../common/constants/GlobalConstants'

const useDASXsig = getVarValue(FF_DAS_XSIG, false)
const includeClaims = getVarValue(FF_USE_LOCAL_SUBSCRIPTION_SERVICE, false)

/**
 * Created by sswaminathan on 12/20/21
 * File: SubscriptionService
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

// import statement lib, local then scss or css

//const SUBSCRIPTION_URL_KEY = 'subscription_base_url'
const SUB_SVC_URL_KEY = 'subscription_service'
const SubscriptionAPI = axios.create({
  baseURL: getFrameworkServiceUrl(SUB_SVC_URL_KEY) || getVarValue(SUB_SVC_URL_KEY),
  timeout: 300 * 1000,
})

SubscriptionAPI.interceptors.request.use(async config => {
  const token = await getTsgToken(config?.tsg_id)
  config.headers = { ...config.headers, 'Authorization': `Bearer ${token}` }
  if (includeClaims) {
    config.headers.claims = token.split('.')[1]
  }
  return config
}, error => {
  return Promise.reject(error)
})

SubscriptionAPI.interceptors.response.use((response) => {
  return response
}, (error) => {
  const { application_code, error_message, message, msg, payload } = error?.response?.data || {}
  if (payload?.session_id) { // error tracking with session_id
    throw Object.assign({ ...payload, application_code, message })
  }
  const userMessage = application_code?.length ? [application_code, (payload?.userMessage || message || error_message || msg)].join(': ') :
    payload?.userMessage || message || error_message || msg
  throw Object.assign({ message: userMessage || error.message, response: { status: error?.response?.status, data: error?.response?.data } })
})

const SubscriptionService = {
  listLicense: {
    get: ({ tsg_id, hash, flow_type, license_context }) => SubscriptionAPI.get(LICENSES_URL, {
      params: {
        hash: hash,
        flow_type: flow_type,
        license_context: license_context,
        is_api_vnext: false,
      }, tsg_id,
    }),
    post: (request, tsg_id) => SubscriptionAPI.post(CLAIMS_URL, request, { tsg_id }),
    put: (request, tsg_id) => SubscriptionAPI.put(CLAIMS_URL, request, { tsg_id }),
  },
  reClaim: {
    put: (request, tsg_id) => SubscriptionAPI.put(RECLAIM_URL, request, { tsg_id }),
  },
  listLicenseForTsg: {
    get: ({ tsg_id, ancestor }) => SubscriptionAPI.get(CLAIMS_URL,
      { params: { ancestor: !ancestor }, tsg_id }),
  },
  listCspAccounts: {
    get: ({ tsg_id, email }) => SubscriptionAPI.get(SUPPORT_ACCOUNTS, { params: { email: email }, tsg_id: TSG_NONE.id }),
  },
  validateUrl: {
    post: ({ request, tsg_id, app_id }) => SubscriptionAPI.post(`/validate/${app_id}/subdomain`, request, { tsg_id }),
  },
  prismaAccessValidation: {
    post: ({ request, tsg_id }) => SubscriptionAPI.post(PAE_VALIDATION_URL, request, { tsg_id }),
  },
  submitAllocation: {
    post: ({ request, tsg_id }) => SubscriptionAPI.post(LICENSE_QUOTA_URL, request, { tsg_id }),
    put: ({ request, tsg_id }) => SubscriptionAPI.put(LICENSE_QUOTA_URL, request, { tsg_id }),
  },
  modifySubscription: {
    post: ({ request, tsg_id }) => SubscriptionAPI.post(MODIFY_SUBSCRIPTION_URL, request, { tsg_id }),
  },
  createTsg: {
    post: ({ request, tsg_id }) => SubscriptionAPI.post(LICENSE_QUOTA_URL, request, { tsg_id }),
  },
  instancesFromCSP: {
    get: ({ app_id, tsg_id, csp_id }) => SubscriptionAPI.get(`${SUPPORT_ACCOUNTS}/${csp_id}/${app_id}`, { tsg_id })
  },
  activate: {
    post: ({ tsg_id, request, headers = {} }) => SubscriptionAPI.post(ACTIVATE_URL, request, { tsg_id, headers }),
  },
  instances: {
    list: ({ tsg_id }) => SubscriptionAPI.get(INSTANCE_URL, { tsg_id }),
    create: ({ tsg_id, request }) => SubscriptionAPI.post(INSTANCE_URL, request, { tsg_id }),
    delete: ({ instance_id, tsg_id, params }) => SubscriptionAPI.delete(`${INSTANCE_URL}/${instance_id}`, { tsg_id, params: params }),
    patch: ({ instance_id, tsg_id, payload }) => SubscriptionAPI.patch(`${INSTANCE_URL}/${instance_id}`, payload, { tsg_id })
  },
  validate: {
    // /validate/panorama
    panorama: ({
      tsg_id,
      request: { serial_number, support_account_id, register_new, has_support_license, cdl_tenant_id },
    }) => SubscriptionAPI.post(VALIDATE_PANORAMA_URL, {
      serial_number,
      support_account_id,
      register_new,
      has_support_license,
      cdl_tenant_id
    }, { tsg_id }),
  },
  cspTsgMapping: {
    get: ({ supportAccountId }) => SubscriptionAPI.get(`/support_accounts/${supportAccountId}/tenant-service-groups`, { tsg_id: TSG_NONE.id })
  },
  devices: {
    list: async ({ tsg_id }) => {
      await new Promise(resolve => setTimeout(() => resolve(true), 1500))
      // Normal Response
      const data = [...MOCK_LIST_DEVICES]
      for (let i = 0; i < 1000; ++i) {
        data.push({
          'device_type': 'ngfw',
          'serial_number': i,
          'tsg_id': '2345',
          'model': 'PAN-PA-220',
          'support_account_name': `MOCK+${i}`,
          'support_account_id': '1889',
          'created_by': 'test@domain.com',
          'extra': {},
          'associations': [],
        })
      }
      return {
        data: {
          count: data.length,
          items: data.map(item => Object.assign({}, item, { tsg_id })),
        },
      }
    },
    listSupportAccountDevices: async ({ tsg_id, support_account_id, x_sig }) => {
      // real
      const params = { support_account_id }
      if (useDASXsig) {
        params['x-sig'] = x_sig
      }
      return SubscriptionAPI.get(SUPPORT_DEVICES, { params, tsg_id })
    },
    listTenantDevices: ({ tsg_id }) => {
      const params = {
        device_types: DAS_FILTER_DEVICE_TYPES.join(',')
      }
      return SubscriptionAPI.get(TENANT_DEVICES, { params, tsg_id })
    },
    listNotEligibleDASApps: ({ tsg_id }) => SubscriptionAPI.get(NOT_ELIGIBLE_APPS_DEVICE_ASSOCIATION, { tsg_id }),
    associateDevices: ({
      tsg_id,
      support_account_id,
      devices,
      x_sig
    }) => {
      const data = { support_account_id, devices }
      if (useDASXsig) {
        data['x-sig'] = x_sig
      }
      return SubscriptionAPI.post(TENANT_DEVICES, data, { tsg_id })
    },
    dissociateDevices: ({
      tsg_id,
      support_account_id,
      devices,
    }) => SubscriptionAPI.delete(TENANT_DEVICES, { tsg_id, data: { support_account_id, devices } }),
    associateApps: ({
      tsg_id,
      associations,
      x_sig,
      is_eula_accepted
    }) => {
      const data = { associations, 'x-sig': x_sig, is_eula_accepted }
      return SubscriptionAPI.post(APP_DEVICE_ASSOCIATION, { ...data }, { tsg_id })
    },
    dissociateApps: ({
      tsg_id,
      associations,
    }) => SubscriptionAPI.delete(APP_DEVICE_ASSOCIATION, { tsg_id, data: { associations } }),
    generatePanoramaOTP: ({
      tsg_id,
      sn,
      support_account_id,
      xsig,
    }) => SubscriptionAPI.post(PANORAMA_OTP, {
      sn,
      support_account_id,
      xsig,
    }, { tsg_id }),
  },
}

SubscriptionService.propTypes = {}
export default SubscriptionService
